import { Option, ReadonlyArray, Runtime } from "fp";

import React from "react";

import { Order } from "~/Order";

export type Version = { readonly version: readonly number[] };

export function Version(version: Version) {
  return <>{Version.toString(version)}</>;
}

export namespace Version {
  export const { equals, compare } = Order.fromCompare<Version>((a, b) =>
    Order.string().compare(Padded.fromVersion(a), Padded.fromVersion(b))
  );

  export const zero = (): Version => ({ version: [0, 0, 0] });

  export const isOutdated = (latest: Version) => (current: Version): boolean =>
    compare(current, latest) < 0;

  export const fromString = (string: string): Version | undefined => {
    const [major, minor, patch, ...rest] = pipe(
      string.replace(/\D/g, ".").split("."),
      ReadonlyArray.filterMap((part) =>
        pipe(parseInt(part, 10), (number) =>
          isNaN(number) ? Option.none : Option.some(number)
        )
      )
    );

    return major !== undefined || minor !== undefined || patch !== undefined
      ? { version: [major, minor, patch, ...rest] }
      : undefined;
  };

  export const toString = ({
    version: [major = 0, minor = 0, patch = 0, part4],
  }: Version): string =>
    `${[major, minor, patch].join(".")}${part4 ? ` v${part4}` : ""}`;

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({ last_version: Runtime.nullable(Runtime.string()) });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Version | undefined =>
      response.last_version ? fromString(response.last_version) : undefined;
  }

  export type Padded = string;
  export namespace Padded {
    export const fromVersion = ({
      version: [major, minor, patch, part4 = 0, part5 = 0, part6 = 0],
    }: Version): Padded =>
      major === undefined || minor === undefined || patch === undefined
        ? fromVersion({ version: [0, 0, 0, part4, part5, part6] })
        : [major, minor, patch, part4, part5, part6]
            .map((part) => `${part}`.padStart(4))
            .join(".");
  }
}
